import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, XCircle, Loader2, Coins } from 'lucide-react';
import { billingApi } from '@/api/billing';

const TERMINAL = ['CREDITED', 'FAILED', 'CANCELED', 'REFUNDED'];

export default function BillingCallbackPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const orderId = params.get('orderId') ?? '';

  const { data: order } = useQuery({
    queryKey: ['billing-order', orderId],
    queryFn: () => billingApi.getOrder(orderId),
    enabled: !!orderId,
    // Поллим, пока заказ не дойдёт до терминального статуса (вебхук может
    // прийти на backend с задержкой в пару секунд после редиректа).
    refetchInterval: (query) => {
      const s = query.state.data?.status;
      return s && TERMINAL.includes(s) ? false : 2000;
    },
  });

  // При зачислении обновляем баланс/историю в кэше.
  useEffect(() => {
    if (order?.status === 'CREDITED') {
      qc.invalidateQueries({ queryKey: ['swipa-balance'] });
      qc.invalidateQueries({ queryKey: ['billing-orders'] });
    }
  }, [order?.status, qc]);

  const status = order?.status;
  const isCredited = status === 'CREDITED';
  const isFailed = status === 'FAILED' || status === 'CANCELED';
  const isPending = !status || status === 'PENDING' || status === 'PAID';

  return (
    <div className="flex-1 flex flex-col items-center justify-center bg-surface-950 px-6 text-center">
      <div className="max-w-sm w-full bg-surface-900 border border-surface-800 rounded-2xl p-8 flex flex-col items-center">
        {!orderId ? (
          <>
            <XCircle size={48} className="text-red-400 mb-4" />
            <h2 className="text-lg font-bold text-surface-100">Заказ не найден</h2>
          </>
        ) : isPending ? (
          <>
            <Loader2 size={48} className="text-brand-400 mb-4 animate-spin" />
            <h2 className="text-lg font-bold text-surface-100">Подтверждаем оплату…</h2>
            <p className="text-surface-400 text-sm mt-2">Это занимает несколько секунд. Не закрывайте страницу.</p>
          </>
        ) : isCredited ? (
          <>
            <CheckCircle2 size={48} className="text-green-400 mb-4" />
            <h2 className="text-lg font-bold text-surface-100">Баланс пополнен!</h2>
            <p className="text-surface-300 text-sm mt-2 flex items-center gap-1.5 justify-center">
              <Coins size={15} className="text-yellow-400" />
              Зачислено <span className="font-bold text-yellow-400">{order?.swipaAmount.toLocaleString('ru-RU')} SWIPA</span>
            </p>
          </>
        ) : isFailed ? (
          <>
            <XCircle size={48} className="text-red-400 mb-4" />
            <h2 className="text-lg font-bold text-surface-100">
              {status === 'CANCELED' ? 'Оплата отменена' : 'Оплата не прошла'}
            </h2>
            <p className="text-surface-400 text-sm mt-2">Средства не списаны. Попробуйте ещё раз.</p>
          </>
        ) : null}

        <div className="flex gap-2 mt-6 w-full">
          <button onClick={() => navigate('/billing')} className="btn-ghost flex-1 py-2 text-sm">
            К пакетам
          </button>
          <button onClick={() => navigate('/')} className="btn-primary flex-1 py-2 text-sm">
            В приложение
          </button>
        </div>
      </div>
    </div>
  );
}
